<template>
  <i
    v-if="isRelevant"
    class="tm-scroll-sensor"
    :class="{
      v: MODE_DIAGNOSTIC,
    }"
    :style="styleObj"
  />
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import gridMixin from '../grid.mixin';
import { TimePoint } from '../../api/grid';

export default {
  name: 'tm-scroll-sensor',
  mixins: [gridMixin],

  data() {
    return {
      timePoint: new TimePoint(),
    };
  },

  computed: {
    ...mapGetters('timemachine', ['grid']),
    ...mapState('timemachine', {
      pxRowHeight: (state) => state.cssVariables.pxRowHeight,
      pxAssetWidth: (state) => state.cssVariables.pxAssetWidth,
      useScrollSensor: (state) => state.dndEscort.useScrollSensor,
      MODE_DIAGNOSTIC: (state) => state.MODE.DIAGNOSTIC,
      axis: (state) => state.viewOffsets.axis,
    }),

    isRelevant() {
      return this.gridEstablished && this.useScrollSensor;
    },

    styleObj() {
      // to prevent scrollable container to scroll beyond his rendered content
      // - adjust center position under mouse so it won't exit the viewOffsets

      const height = Math.min(0.8 * this.axis.height, 0.8 * this.pxRowHeight);
      const box = { width: this.pxAssetWidth, height: height };
      const side = {
        left: box.width / 2,
        right: box.width / 2,
        top: box.height / 2,
        bottom: box.height / 2,
      };
      const shift = { left: side.left, top: side.top };
      const row = {
        first: this.grid[0],
        last: this.grid[this.grid.length - 1],
      };
      const min = { x: row.first.p1.x, y: row.first.p1.y };
      const max = { x: row.first.p2.x, y: row.last.p2.y };

      if (this.$data.timePoint.y - side.top < min.y) {
        shift.top -= min.y - (this.$data.timePoint.y - side.top);
      }
      if (this.$data.timePoint.x + side.right > max.x) {
        shift.left += this.$data.timePoint.x + side.right - max.x;
      }
      if (this.$data.timePoint.y + side.bottom > max.y) {
        shift.top += this.$data.timePoint.y + side.bottom - max.y;
      }
      if (this.$data.timePoint.x - side.left < min.x) {
        shift.left -= min.x - (this.$data.timePoint.x - side.left);
      }

      return {
        top: `${this.$data.timePoint.y}px`,
        left: `${this.$data.timePoint.x}px`,
        width: `${box.width}px`,
        height: `${box.height}px`,
        transform: `translate(${-shift.left}px, ${-shift.top}px)`,
      };
    },
  },

  watch: {
    isRelevant(value) {
      if (value) {
        window.addEventListener('mousemove', this.$_onMousemove);
      } else {
        window.removeEventListener('mousemove', this.$_onMousemove);
      }
    },
  },

  beforeDestroy() {
    window.removeEventListener('mousemove', this.$_onMousemove);
  },

  methods: {
    $_onMousemove(e) {
      this.$_mouse2point(e, this.$data.timePoint);
      this.$_scrollIntoView();
    },

    $_scrollIntoView: _.throttle(
      function () {
        // while this is throttled, `this.$el` can be undefined during `isRelevant` changes
        if (this.isRelevant) {
          // 'smooth' - unreliable - may stop working in next Chrome like it did in `84
          this.$el.scrollIntoView({
            behavior: 'auto',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      },
      128,
      { leading: false, trailing: true }
    ),
  },
};
</script>

<style lang="scss" scoped>
.tm-scroll-sensor {
  position: absolute;
  z-index: var(--tm-zScrollSensor);
  pointer-events: none;

  &.v {
    border: 1px dashed blueviolet;
  }
}
</style>
